import isApplicationUneditable from '@bybeam/platform-lib/application/isApplicationUneditable';
import canCreateApplication, {
  ApplicationWithCase,
} from '@bybeam/platform-lib/application/reapplication';
import { checkFeature } from '@bybeam/platform-lib/features/check';
import sort, { Sorter } from '@bybeam/platform-lib/utilities/sort';
import {
  Application,
  CaseStatus,
  FeatureName,
  Nullable,
  Program,
  ScheduleType,
  User,
} from '@bybeam/platform-types';
import {
  findLatestPayment,
  getTotalInitiatedPaymentCount,
  hasAuthorizedPayment,
} from '@platform-ui-common/utilities/payment.js';
import {
  canAccessProgram,
  isProgramOpen,
  programHasBeamPayments,
} from '@platform-ui/utilities/programs.js';
import dayjs from 'dayjs';

export enum StatusBlockAction {
  Claim = 'Claim',
  ContinueApplication = 'ContinueApplication',
  StartApplication = 'StartApplication',
  ViewDetails = 'ViewDetails',
}

export interface StatusBlock {
  program: Program;
  application?: Application;
  action: StatusBlockAction;
  expiration?: dayjs.Dayjs;
}

export enum StatusBlockSection {
  ActionRequired = 'Action Required',
  AvailablePrograms = 'Available Programs',
  SubmittedPrograms = 'Submitted Programs',
}

export type StatusBlocks = { [section in StatusBlockSection]: StatusBlock[] };

const BASE_SORT_ORDERS: Sorter<StatusBlock>[] = [
  { accessor: ({ program }) => program.config?.sortOrder },
  { accessor: ({ program }) => program.name },
];

function mapApplicationExpirationStart({
  case: { decisionReachedAt, fulfillments, program, status, statusUpdatedAt },
}: Application): Nullable<Date> {
  switch (status) {
    // biome-ignore lint/suspicious/noFallthroughSwitchClause: intentional
    case CaseStatus.Approved:
      if (!checkFeature(program?.features, FeatureName.PaymentsExternalTracking)) break;
    case CaseStatus.Denied:
    case CaseStatus.FiscalReview:
      return decisionReachedAt;
    case CaseStatus.Withdrawn:
      return statusUpdatedAt;
    case CaseStatus.PaymentSent: {
      const [fulfillment] = fulfillments ?? [];
      const latestPayment = findLatestPayment(fulfillment?.payments ?? []);

      if (fulfillment?.scheduleType !== ScheduleType.Recurring)
        return latestPayment?.initiatedAt ?? decisionReachedAt;

      if (
        getTotalInitiatedPaymentCount(fulfillment?.payments) === fulfillment?.paymentPattern?.count
      )
        return latestPayment.initiatedAt;
    }
  }
}

function mapApplicationAction(
  { case: { fulfillments, program, status }, submittedAt }: Application,
  user: User,
): Nullable<StatusBlockAction> {
  switch (status) {
    // biome-ignore lint/suspicious/noFallthroughSwitchClause: intentional
    case CaseStatus.Withdrawn:
      if (submittedAt) break;
    case CaseStatus.InProgress:
    case CaseStatus.Incomplete:
      return StatusBlockAction.ContinueApplication;
    // biome-ignore lint/suspicious/noFallthroughSwitchClause: intentional
    case CaseStatus.Approved:
      if (
        checkFeature(program?.features, FeatureName.PaymentsClaimFunds) &&
        hasAuthorizedPayment(fulfillments[0])
      )
        return fulfillments[0].payments[0].payee.id === user.id
          ? StatusBlockAction.Claim
          : undefined;
    case CaseStatus.PaymentSent:
      if (programHasBeamPayments(program)) return StatusBlockAction.ViewDetails;
  }
}

function getApplicationBlocks(
  programs: Program[],
  applications: Application[],
  user: User,
): StatusBlock[] {
  const expiration = (windowStart: Nullable<Date>) => {
    if (!windowStart) return undefined;
    return dayjs(windowStart).add(14, 'day');
  };

  return applications
    .filter(
      (app) =>
        app.case.status !== CaseStatus.Archived &&
        (isProgramOpen(app.case?.program, user) ||
          isApplicationUneditable(app as ApplicationWithCase) ||
          !!app.submittedAt),
    )
    .map((app) => ({
      application: app,
      program: programs.find((program) => program.id === app.case?.program.id) || app.case?.program,
      action: mapApplicationAction(app, user),
      expiration: expiration(mapApplicationExpirationStart(app)),
    }));
}

function getAvailableProgramBlocks(
  programs: Program[],
  applications: Application[],
  user: User,
): StatusBlock[] {
  return sort(
    programs
      .filter(
        (program) =>
          canAccessProgram(program, user) &&
          canCreateApplication(program, applications as ApplicationWithCase[], user),
      )
      .map((program) => ({ program, action: StatusBlockAction.StartApplication })),
    {
      accessor: ({ program: { id } }) =>
        user.referrals?.filter(({ program }) => program.id === id)?.length ?? 0,
      ascending: false,
    },
    ...BASE_SORT_ORDERS,
  );
}

export default function getStatusBlocks(
  programs: Program[],
  applications: Application[],
  user: User,
): StatusBlocks {
  const blocks = getApplicationBlocks(programs, applications, user);

  return {
    [StatusBlockSection.ActionRequired]: sort(
      blocks.filter(({ action }) =>
        [StatusBlockAction.ContinueApplication, StatusBlockAction.Claim].includes(action),
      ),
      { accessor: ({ action }) => action },
      ...BASE_SORT_ORDERS,
    ),
    [StatusBlockSection.AvailablePrograms]: getAvailableProgramBlocks(programs, applications, user),
    [StatusBlockSection.SubmittedPrograms]: sort(
      blocks
        .filter(({ action }) => !action || action === StatusBlockAction.ViewDetails)
        .filter(({ expiration }) => !expiration || dayjs().isBefore(expiration)),
      ...BASE_SORT_ORDERS,
    ),
  };
}
